import React from "react";
import { useTranslation } from "react-i18next";

import AppCheckbox from "../design-system/components/AppCheckbox";
import AppInput from "../design-system/components/AppInput";
import AppSwitch from "../design-system/components/AppSwitch";
import AppText from "../design-system/components/AppText";
import "./WorkingDayDefaultsEditor.css";

/*
  Arbeitstage (Standard) mit Zeiten pro Tag — die gemeinsame Erfassung fuer
  /availability (Benutzer-Dialog) und /team/x/edit. Gegenstelle der Regel in
  databases/availability_defaults.py: nur gelistete Wochentage sind Arbeitstage,
  jeder ist ganztags oder traegt genau ein Von–Bis.

  value: [{ weekday, isFullDay, startTime, endTime }] — nur Arbeitstage.
*/

const DEFAULT_START_TIME = "08:00";
const DEFAULT_END_TIME = "17:00";

export function buildWorkingDayPayload(value) {
  return (Array.isArray(value) ? value : []).map((day) => ({
    weekday: day.weekday,
    is_full_day: Boolean(day.isFullDay),
    start_time: day.isFullDay ? "" : day.startTime || "",
    end_time: day.isFullDay ? "" : day.endTime || "",
  }));
}

export function workingDaysFromApi(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    weekday: Number(row.weekday),
    isFullDay: Boolean(row.is_full_day),
    startTime: row.start_time || "",
    endTime: row.end_time || "",
  }));
}

/**
 * Haben sich die Arbeitstage wirklich geändert?
 *
 * Ist noch nichts gespeichert, zeigen die Dialoge die wirksame Rückfallebene des
 * Modus vorbelegt an. Diese Anzeige darf beim Speichern nicht mitgeschrieben
 * werden — sonst werden aus „nie festgelegt" echte Arbeitstage, und die schlagen
 * ab dann den Verfügbarkeitsmodus.
 */
export function hasWorkingDaysChanged(initialDraft, draft) {
  const normalize = (value) => JSON.stringify(
    buildWorkingDayPayload(value).sort((a, b) => a.weekday - b.weekday)
  );
  return normalize(initialDraft?.working_days) !== normalize(draft?.working_days);
}

/** Zeiten unvollständig oder Ende nicht nach Start? Dann ist Speichern zu früh. */
export function hasInvalidWorkingDayTimes(value) {
  return (Array.isArray(value) ? value : []).some((day) => {
    if (day.isFullDay) return false;
    if (!day.startTime || !day.endTime) return true;
    return day.endTime <= day.startTime;
  });
}

export default function WorkingDayDefaultsEditor({ value, onChange, disabled = false }) {
  const { t } = useTranslation();
  const weekdayNames = t("availability.weekdays.full", { returnObjects: true });
  const days = Array.isArray(value) ? value : [];
  const dayByWeekday = new Map(days.map((day) => [day.weekday, day]));

  const toggleWeekday = (weekday) => {
    if (dayByWeekday.has(weekday)) {
      onChange(days.filter((day) => day.weekday !== weekday));
      return;
    }
    onChange(
      [...days, { weekday, isFullDay: true, startTime: "", endTime: "" }].sort(
        (left, right) => left.weekday - right.weekday
      )
    );
  };

  const updateDay = (weekday, patch) => {
    onChange(days.map((day) => (day.weekday === weekday ? { ...day, ...patch } : day)));
  };

  const toggleFullDay = (weekday, isFullDay) => {
    const day = dayByWeekday.get(weekday);
    if (!day) return;
    updateDay(weekday, {
      isFullDay,
      // Beim Wechsel auf Zeiten vorbefuellen, damit kein leeres Pflichtfeld
      // das Speichern blockiert, ohne dass man sieht warum.
      startTime: isFullDay ? "" : day.startTime || DEFAULT_START_TIME,
      endTime: isFullDay ? "" : day.endTime || DEFAULT_END_TIME,
    });
  };

  return (
    <div className="working-day-defaults">
      {weekdayNames.map((label, weekday) => {
        const day = dayByWeekday.get(weekday);
        const isWorkingDay = Boolean(day);
        const hasTimeError = Boolean(
          day && !day.isFullDay && day.startTime && day.endTime && day.endTime <= day.startTime
        );

        return (
          <div
            key={label}
            className={`working-day-defaults__row${isWorkingDay ? " working-day-defaults__row--active" : ""}`}
          >
            <AppCheckbox
              className="working-day-defaults__day"
              label={label}
              labelVariant="body-1"
              checked={isWorkingDay}
              disabled={disabled}
              onChange={() => toggleWeekday(weekday)}
            />
            {isWorkingDay ? (
              <div className="working-day-defaults__details">
                <AppSwitch
                  className="working-day-defaults__full-day"
                  label={t("working_day_defaults.full_day", { defaultValue: "Ganztags" })}
                  checked={day.isFullDay}
                  disabled={disabled}
                  onChange={(event) => toggleFullDay(weekday, event.target.checked)}
                />
                {!day.isFullDay ? (
                  <div className="working-day-defaults__times">
                    <AppInput
                      type="time"
                      size="sm"
                      value={day.startTime}
                      disabled={disabled}
                      invalid={hasTimeError}
                      aria-label={t("working_day_defaults.start_aria", {
                        defaultValue: "Verfügbar ab ({{day}})",
                        day: label,
                      })}
                      onChange={(event) => updateDay(weekday, { startTime: event.target.value })}
                    />
                    <AppText variant="body-2" tone="secondary">–</AppText>
                    <AppInput
                      type="time"
                      size="sm"
                      value={day.endTime}
                      disabled={disabled}
                      invalid={hasTimeError}
                      aria-label={t("working_day_defaults.end_aria", {
                        defaultValue: "Verfügbar bis ({{day}})",
                        day: label,
                      })}
                      onChange={(event) => updateDay(weekday, { endTime: event.target.value })}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
